// Library methods
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import { CSVLink } from "react-csv";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// MUI Components
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import DefaultModal from "../../../../components/UI/DefaultModal";
import { DownloadIcon } from "../../../../assets/icons";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../../styles/muiStylesHelper";
import Alert from "../../../../components/UI/Alert";

// Dropzone extensions
const dropzoneExtensions = {
  "text/csv": [
    ".csv",
    "text/csv",
    "application/vnd.ms-excel",
    "application/csv",
    "text/x-csv",
    "application/x-csv",
    "text/comma-separated-values",
    "text/x-comma-separated-values",
  ],
};

// receives the csv data, and converts it into a json structure
function csvJSON(csv) {
  const lines = csv.split("\n");
  const result = [];

  // the users' info starts from the third line
  for (let i = 2; i < lines.length; i++) {
    const userObj = { firstName: undefined, lastName: undefined };
    const currentLine = lines[i].split(",");

    // in case there is a blank line, skip this iteration
    if (currentLine.length === 1 && !currentLine[0]) continue;

    // removing the initial and ending quotes from the strings
    userObj.firstName = currentLine[0].replace(/(^"|')|("|'$)/g, "");
    userObj.lastName = currentLine[1].replace(/(^"|')|("|'$)/g, "");

    result.push(userObj);
  }

  return result;
}

const UploadPatientsModal = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  usersToUpload,
  setUsersToUpload,
}) => {
  // internationalization
  const { t } = useTranslation();

  // state to control the display text messages
  const [emptyFile, setEmptyFile] = useState(false);
  const [wrongExtension, setWrongExtension] = useState(false);

  // modal fullScreen on small screens
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // csv link ref
  const csvLinkRef = useRef(null);

  const onFileDialogCancel = () => {
    setEmptyFile(false);
    setUsersToUpload([]);
    setWrongExtension(false);
  };

  const onDropRejected = () => {
    setEmptyFile(false);
    setUsersToUpload([]);
    setWrongExtension(true);
  };

  const onDropAccepted = () => {
    setEmptyFile(false);
    setUsersToUpload([]);
    setWrongExtension(false);
  };

  const onCloseWrapper = () => {
    setEmptyFile(false);
    setUsersToUpload([]);
    setWrongExtension(false);
    onClose();
  };

  const onCancelWrapper = () => {
    setEmptyFile(false);
    setUsersToUpload([]);
    setWrongExtension(false);
    onCancel();
  };

  // csv constants
  const csvTemplate = [
    [t("patients_bulk_upload_modal_csv_description")],
    [
      t("patients_bulk_upload_modal_csv_column_one"),
      t("patients_bulk_upload_modal_csv_column_two"),
    ],
    [
      t("patients_bulk_upload_modal_csv_column_one_example"),
      t("patients_bulk_upload_modal_csv_column_two_example"),
    ],
  ];

  return (
    <DefaultModal
      isOpen={open}
      onClose={onCloseWrapper}
      title={t("patients_bulk_upload_modal_title")}
      maxWidth="xs"
      fullScreen={fullScreen}
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button
            variant="text"
            sx={ModalCustomCancel}
            onClick={onCancelWrapper}
          >
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            disabled={!(usersToUpload.length > 0)}
            onClick={onConfirm}
          >
            {t("button_confirm")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        <Typography
          id="modal-modal-description"
          variant="body1"
          component="div"
        >
          {t("patients_bulk_upload_modal_description")}
        </Typography>
        <Grid item xs={12} display="flex" justifyContent="center">
          <Button
            variant="outlined"
            sx={{
              height: 50,
              color: "white",
              borderRadius: "4px",
              borderColor: "#202338",
              backgroundColor: "#202338",
              "&:hover": {
                color: "white",
                backgroundColor: "#202338",
                borderColor: "#202338",
              },
            }}
            onClick={() => csvLinkRef?.current?.link.click()}
          >
            <DownloadIcon />
            <Typography sx={{ ml: "8px" }}>{t("button_template")}</Typography>
          </Button>
          <CSVLink
            ref={csvLinkRef}
            data={csvTemplate}
            filename={`${t(
              "patients_bulk_upload_modal_template_filename"
            )}.csv`}
          />
        </Grid>
        <Grid
          item
          xs={12}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Dropzone
            accept={dropzoneExtensions}
            onDrop={async ([file]) => {
              const reader = new FileReader();
              reader.onload = function (e) {
                const csvContent = e.target.result;
                const convertedCsv = csvJSON(csvContent);
                if (convertedCsv.length === 0) setEmptyFile(true);
                setUsersToUpload(convertedCsv);
              };
              reader.readAsText(file);
            }}
            onDropAccepted={onDropAccepted}
            onDropRejected={onDropRejected}
            onFileDialogCancel={onFileDialogCancel}
          >
            {({ acceptedFiles, getRootProps, getInputProps }) => (
              <>
                <div
                  {...getRootProps()}
                  style={{
                    border: "2px dashed #202338",
                    padding: "50px 30px",
                    borderRadius: "8px",
                  }}
                >
                  <input {...getInputProps()} />
                  {t("patients_bulk_upload_modal_dropzone")}
                </div>
                {acceptedFiles.length > 0 && (
                  <Typography variant="body1" component="div" mt={2}>
                    <b>{t("patients_bulk_upload_modal_file")}:</b>{" "}
                    {acceptedFiles[0].path}
                  </Typography>
                )}
                {emptyFile && (
                  <Alert
                    variant="error"
                    alertContent={
                      <Typography fontSize="14px">
                        {t(
                          "patients_bulk_upload_modal_message_missing_user_info"
                        )}
                      </Typography>
                    }
                    mt="20px"
                  />
                )}
                {wrongExtension && (
                  <Alert
                    variant="error"
                    alertContent={
                      <Typography fontSize="14px">
                        {t(
                          "patients_bulk_upload_modal_message_wrong_extension"
                        )}
                      </Typography>
                    }
                    mt="20px"
                  />
                )}
              </>
            )}
          </Dropzone>
        </Grid>
      </Grid>
    </DefaultModal>
  );
};

export default UploadPatientsModal;
